/**
 * Element-Sitz Invite — Parser (kein Firma-Homeserver).
 * Vertrag: docs/handoffs/LABOR_SERVER_HANDOFF.md §5h (element_seat / element_rooms).
 * Stub-Katalog nur als Negativliste — UI darf ihn nicht als Fallback anbieten
 * (Klasse MATRIX_ROOM_FIELD_CANON / ehrlicher Block).
 */

import {
  buildElementKanzleiBootUrl,
  isHonestMatrixRoomId,
  isMatrixUserId,
  isStubMatrixRoomId,
} from './portalMatrixOrigins.ts';

export type ElementRoomOption = {
  id: string;
  alias: string;
  name: string;
  /** Store-Zeile aus der Serverauslieferung. Weitere Räume bleiben false. */
  is_standard?: boolean;
  /** Leer = Kanzlei-Raum. Gesetzt = Akte-Raum aus dem Store. */
  akte_id?: string;
  /**
   * Nur gesetzt, wenn GET `rooms[].seat_ids` mitliefert.
   * Fehlt das Feld oder ist es leer: niemand zum Anzeigen.
   */
  seat_ids?: string[];
};

/** Historische Stub-IDs — VERBOTEN als Deep-Link / Invite-Default. */
export const LABOR_ELEMENT_ROOM_CATALOG: ElementRoomOption[] = [
  {
    id: '!labor-allgemein:labor.element.stub',
    alias: '#allgemein:labor.element.stub',
    name: 'Kanzlei allgemein',
  },
  {
    id: '!labor-team:labor.element.stub',
    alias: '#team:labor.element.stub',
    name: 'Kanzlei intern',
  },
  {
    id: '!labor-mandanten:labor.element.stub',
    alias: '#mandanten:labor.element.stub',
    name: 'Mandanten-Koordination',
  },
];

/** Kundenlabel, wenn der Sitz nur die Room-Id trägt (kein Stub-Katalog). */
export const KANZLEI_CHAT_ROOM_LABEL = 'Kanzlei-Chat';

export function filterHonestElementRooms(rooms: ElementRoomOption[]): ElementRoomOption[] {
  return rooms.filter((room) => isHonestMatrixRoomId(room.id) && !isStubMatrixRoomId(room.id));
}

/** GET-Katalog: Collection-Zeilen. Stub-IDs bleiben draußen. */
export function readElementRoomCatalog(rows: unknown): ElementRoomOption[] {
  if (!Array.isArray(rows)) return [];
  const parsed: ElementRoomOption[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const id = typeof rec.id === 'string' ? rec.id.trim() : '';
    if (!id || !isHonestMatrixRoomId(id) || isStubMatrixRoomId(id)) continue;
    const name = typeof rec.name === 'string' ? rec.name.trim() : '';
    const room: ElementRoomOption = {
      id,
      alias: typeof rec.alias === 'string' ? rec.alias : '',
      name,
      is_standard: rec.is_standard === true,
    };
    if (typeof rec.akte_id === 'string') room.akte_id = rec.akte_id.trim();
    if (Array.isArray(rec.seat_ids)) room.seat_ids = rec.seat_ids.map((value) => String(value ?? ''));
    parsed.push(room);
  }
  return parsed;
}

/**
 * GET-Körper. Nur `source: "store"`. Stub-Quelle und fehlendes source sind leer.
 * Archivierte Zeilen kommen vom Hook nicht mit; Stub-IDs fallen im Katalogparser.
 */
export function readElementRoomsResponse(data: unknown): ElementRoomOption[] {
  if (!data || typeof data !== 'object') return [];
  const rec = data as Record<string, unknown>;
  if (rec.source !== 'store') return [];
  return readElementRoomCatalog(rec.rooms);
}

function elementApiFailureText(rec: Record<string, unknown>, fallback: string): string {
  const err = typeof rec.error === 'string' ? rec.error.trim() : '';
  const message = typeof rec.message === 'string' ? rec.message.trim() : '';
  const text = err || message;
  if (!text || text.length > 200) return fallback;
  const secretKeys = ['access_token', 'login_token', 'loginToken', 'password', 'matrix_password'] as const;
  for (let i = 0; i < secretKeys.length; i++) {
    const raw = rec[secretKeys[i]];
    if (typeof raw === 'string' && raw.trim() && text.includes(raw.trim())) return fallback;
  }
  return text;
}

export function readSeatMatrixRoomId(record: Record<string, unknown>): string {
  const camel = typeof record.matrixRoomId === 'string' ? record.matrixRoomId : '';
  const snake = typeof record.matrix_room_id === 'string' ? record.matrix_room_id : '';
  const id = (snake || camel).trim();
  if (!isHonestMatrixRoomId(id) || isStubMatrixRoomId(id)) return '';
  return id;
}

/** Felder, die eine User-Id tragen dürfen. Room-Felder gehören nicht dazu. */
const SEAT_MXID_KEYS = ['mxid', 'matrix_user_id', 'matrixUserId', 'element_mxid'] as const;

/**
 * MXID des Sitzes. Room-Id (`!…`), Alias und E-Mail sind kein Konto.
 * `office@` wird hier nicht zu einer MXID gemacht.
 */
export function readSeatMatrixUserId(record: Record<string, unknown>): string {
  for (let i = 0; i < SEAT_MXID_KEYS.length; i++) {
    const raw = record[SEAT_MXID_KEYS[i]];
    if (typeof raw !== 'string') continue;
    const id = raw.trim();
    if (isMatrixUserId(id)) return id;
  }
  return '';
}

/** Unter dem Schalter: nur `@user:server`, sonst leer. */
export function seatElementAccountLine(raw: string | undefined | null): string {
  const id = String(raw ?? '').trim();
  return isMatrixUserId(id) ? id : '';
}

export const KANZLEI_CHAT_NO_ELEMENT_ACCOUNT =
  'Für diesen Sitz ist noch kein Element-Konto verbunden.';

export type KanzleiElementFrame =
  | { kind: 'no-account' }
  | { kind: 'logout' }
  | { kind: 'token' }
  | { kind: 'room' };

/**
 * Sichtbares Element-Fenster.
 * Fremde oder unbekannte Browser-Sitzung ist nie dieser Sitz.
 * Token-Login nur, wenn Attach eines geliefert hat — der Wert bleibt aus dieser Entscheidung.
 */
export function resolveKanzleiElementFrame(input: {
  seatMxid: string;
  sessionMxid?: string | null;
  hasLoginToken?: boolean;
}): KanzleiElementFrame {
  const seat = String(input.seatMxid ?? '').trim();
  if (!isMatrixUserId(seat)) return { kind: 'no-account' };
  const session = String(input.sessionMxid ?? '').trim();
  if (session && isMatrixUserId(session) && session === seat) return { kind: 'room' };
  if (input.hasLoginToken === true) return { kind: 'token' };
  return { kind: 'logout' };
}

/** Raum ist gewählt, das Element-Fenster hat den Verlauf nicht geladen. */
export const KANZLEI_CHAT_HISTORY_UNLOADED =
  'Dieser Raum ist gewählt. Der Verlauf wurde nicht geladen.';

/** GET `seat_ids` führt diesen Sitz nicht. Kein Element-Join. */
export const KANZLEI_CHAT_NOT_A_MEMBER =
  'Dieser Sitz ist in diesem Raum nicht Mitglied. Der Chat wird nicht geöffnet.';

/** Verlauf nur, wenn das Fenster die MXID dieses Sitzes ist. */
export const KANZLEI_CHAT_SEAT_MXID_ONLY =
  'Der Verlauf öffnet nur mit dem Element-Konto dieses Sitzes.';

/** Token ist da, das Fenster dieses Sitzes noch nicht. Kein `#/room/`. */
export const KANZLEI_CHAT_SEAT_LOGIN_PENDING =
  'Der Verlauf öffnet, sobald das Element-Konto dieses Sitzes angemeldet ist.';

/**
 * iframe-`load` ist das HTML. `#/start_sso` schreibt `mx_sso_hs_url` erst im
 * laufenden Element. Der Wechsel auf das Token-Dokument wartet diese Spanne,
 * damit Logout nicht mehr dazwischenfunkt. Das ist kein Sitzbeweis.
 */
export const ELEMENT_HOMESERVER_SETTLE_MS = 1500;

/** @deprecated Nur noch der alte Name. Dieselbe Spanne wie der Homeserver-Schritt. */
export const ELEMENT_LOGOUT_SETTLE_MS = ELEMENT_HOMESERVER_SETTLE_MS;

export type KanzleiElementIframePhase = 'homeserver' | 'token' | 'room';

/**
 * Portal-iframe nur die Boot-Seite auf dem Element-Origin.
 * Nie `#/logout`, nie direkt `#/room/`. Token nur als Query.
 * Raum-Query nur bei `seatIsMember` (GET `seat_ids`).
 */
export function kanzleiElementFrameSrc(input: {
  phase?: KanzleiElementIframePhase;
  hostname?: string;
  loginToken?: string;
  matrixUserId?: string;
  roomId?: string;
  seatIsMember?: boolean;
}): string {
  const src = buildElementKanzleiBootUrl({
    hostname: input.hostname,
    loginToken: input.loginToken,
    matrixUserId: input.matrixUserId,
    roomId: input.roomId,
    seatIsMember: input.seatIsMember === true,
  });
  if (!src || src.includes('#/logout') || src.includes('#/room/')) return '';
  return src;
}

/** Join-Beweis: customers.id steht in GET `rooms[].seat_ids`. */
export function seatIdInRoomSeatIds(
  seatId: string | undefined | null,
  seatIds: string[] | undefined | null,
): boolean {
  const id = String(seatId ?? '').trim();
  if (!id || !Array.isArray(seatIds)) return false;
  for (let i = 0; i < seatIds.length; i++) {
    if (String(seatIds[i] ?? '').trim() === id) return true;
  }
  return false;
}

export type KanzleiChatPane =
  | { kind: 'no-account' }
  | { kind: 'pick' }
  | { kind: 'boot'; src: string }
  | { kind: 'unloaded' }
  | { kind: 'not-member' }
  | { kind: 'seat-mxid' };

/**
 * Rechte Fläche nach Klick oder Anlegen.
 * iframe-src nur die Boot-Seite. Nie `#/logout`, nie direkt `#/room/`.
 * Raum-Query nur wenn GET `seat_ids` den Sitz führt. Token nur als Query.
 */
export function resolveKanzleiChatPane(input: {
  seatMxid: string;
  seatId?: string | null;
  memberSeatIds?: string[] | null;
  roomId?: string | null;
  sessionMxid?: string | null;
  hasLoginToken?: boolean;
  loginToken?: string;
  hostname?: string;
  historyFailed?: boolean;
  /** Unbenutzt. Die Boot-Seite entscheidet Login und Raum, nicht der Parent. */
  iframePhase?: KanzleiElementIframePhase;
}): KanzleiChatPane {
  const frame = resolveKanzleiElementFrame({
    seatMxid: input.seatMxid,
    sessionMxid: input.sessionMxid,
    hasLoginToken: input.hasLoginToken,
  });
  if (frame.kind === 'no-account') return { kind: 'no-account' };
  const roomId = String(input.roomId ?? '').trim();
  if (!roomId) return { kind: 'pick' };
  const honest = isHonestMatrixRoomId(roomId) && !isStubMatrixRoomId(roomId);
  if (!honest || input.historyFailed === true) return { kind: 'unloaded' };
  if (!seatIdInRoomSeatIds(input.seatId, input.memberSeatIds)) return { kind: 'not-member' };
  const seat = String(input.seatMxid ?? '').trim();
  const session = String(input.sessionMxid ?? '').trim();
  const foreign = Boolean(session && isMatrixUserId(session) && session !== seat);
  const token = String(input.loginToken ?? '').trim();
  if (foreign && !token) return { kind: 'unloaded' };
  if (!isMatrixUserId(seat)) return { kind: 'seat-mxid' };
  const src = buildElementKanzleiBootUrl({
    hostname: input.hostname,
    loginToken: token,
    matrixUserId: seat,
    roomId,
    seatIsMember: true,
  });
  if (!src || src.includes('#/logout') || src.includes('#/room/')) return { kind: 'unloaded' };
  return { kind: 'boot', src };
}

/** HTTP 200 ohne MXID und Token ist kein Konto. Kein Token in dieser Zeichenkette. */
export const ELEMENT_SEAT_ATTACH_BLOCKED =
  'Element-Konto für diesen Sitz ist noch nicht verbunden.';

/**
 * Schalter AN. Erfolg nur bei HTTP 200 mit echter MXID und login_token.
 * Room-Id, Alias und E-Mail reichen nicht. Passwort in der Antwort ist kein Erfolg.
 */
export function elementSeatAttachResult(
  data: Record<string, unknown> | null | undefined,
  httpStatus?: number,
): { ok: true; matrixUserId: string; loginToken: string } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  if (hasMatrixPasswordLeak(rec)) {
    return { ok: false, message: ELEMENT_SEAT_ATTACH_BLOCKED };
  }
  if (httpStatus != null && httpStatus !== 200) {
    return { ok: false, message: elementApiFailureText(rec, ELEMENT_SEAT_ATTACH_BLOCKED) };
  }
  const matrixUserId = readSeatMatrixUserId(rec);
  const loginToken = readAttachLoginToken(rec);
  const accessToken = typeof rec.access_token === 'string' ? rec.access_token.trim() : '';
  if (!isMatrixUserId(matrixUserId) || !loginToken || (accessToken && accessToken === loginToken)) {
    return { ok: false, message: ELEMENT_SEAT_ATTACH_BLOCKED };
  }
  return { ok: true, matrixUserId, loginToken };
}

/**
 * Schalter AUS. Erfolg nur bei echtem Deactivate.
 * Stub-200 und 200 ohne `deactivated` sind kein Erfolg. Danach setzt die Fläche `element_seat` auf false.
 */
export function elementSeatDeactivateResult(
  data: Record<string, unknown> | null | undefined,
  httpStatus?: number,
): { ok: true } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  if (hasMatrixPasswordLeak(rec)) {
    return { ok: false, message: 'Element für diesen Sitz nicht geändert.' };
  }
  if (
    (httpStatus == null || httpStatus === 200) &&
    rec.ok === true &&
    rec.deactivated === true &&
    rec.element_stub !== true
  ) {
    return { ok: true };
  }
  const fallback =
    httpStatus != null && httpStatus !== 200
      ? `Element für diesen Sitz nicht geändert (HTTP ${httpStatus}).`
      : 'Element für diesen Sitz nicht geändert.';
  return { ok: false, message: elementApiFailureText(rec, fallback) };
}

/** Das Login-Token darf nur für die bereits am Portal-Sitz belegte MXID gelten. */
export function elementSeatAttachMatchesSeat(matrixUserId: string, seatMxid: string): boolean {
  const attached = String(matrixUserId ?? '').trim();
  const expected = String(seatMxid ?? '').trim();
  return isMatrixUserId(attached) && isMatrixUserId(expected) && attached === expected;
}

/** Höchstens ein Attach pro MXID in dieser Spanne. Nicht bei jedem Raumklick. */
export const ELEMENT_SEAT_ATTACH_MIN_INTERVAL_MS = 60_000;

const elementSeatAttachAtByMxid = new Map<string, number>();

/** true, wenn für diese MXID in der letzten Minute kein Attach vorgemerkt ist. */
export function elementSeatAttachDue(mxid: string, nowMs: number): boolean {
  const id = String(mxid ?? '').trim();
  if (!isMatrixUserId(id)) return false;
  const last = elementSeatAttachAtByMxid.get(id);
  if (last == null) return true;
  return nowMs - last >= ELEMENT_SEAT_ATTACH_MIN_INTERVAL_MS;
}

/** Merkt den Zeitpunkt. Der Tokenwert gehört nicht hierher. */
export function markElementSeatAttach(mxid: string, nowMs: number): void {
  const id = String(mxid ?? '').trim();
  if (!isMatrixUserId(id) || !Number.isFinite(nowMs)) return;
  elementSeatAttachAtByMxid.set(id, nowMs);
}

export function clearElementSeatAttachMarks(): void {
  elementSeatAttachAtByMxid.clear();
}

/**
 * Einmal beim Öffnen des Kanzlei-Chats, höchstens jede Minute pro MXID. Nur die Sitz-Id.
 * Vertrag 7e40b06d: bestehendes Konto bleibt, die Antwort kann ein neues login_token tragen.
 */
export function kanzleiChatSeatAttachRequest(customerId: string):
  | { ok: true; body: { customer_id: string } }
  | { ok: false } {
  const customer_id = String(customerId ?? '').trim();
  if (!customer_id) return { ok: false };
  return { ok: true, body: { customer_id } };
}

/** Einmaliges Login-Token aus der Attach-Antwort. Leer, wenn keins da ist. Nicht loggen. */
export function readAttachLoginToken(data: Record<string, unknown>): string {
  const keys = ['login_token', 'loginToken', 'element_login_token'] as const;
  for (let i = 0; i < keys.length; i++) {
    const raw = data[keys[i]];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return '';
}

/**
 * Noch nicht verwendete Einmal-Tokens dieser Seite.
 * Nicht localStorage, nicht die Parent-URL, nicht die Konsole.
 */
const seatLoginTokens = new Map<string, string>();

export function rememberSeatLoginToken(seatId: string, token: string): void {
  const id = seatId.trim();
  const value = token.trim();
  if (!id || !value) return;
  seatLoginTokens.set(id, value);
}

/**
 * Beansprucht das Token genau einmal für einen Boot-Versuch.
 * Der Aufrufer hält es nur im Zustand dieses einen iframes; ein anderer Raum
 * oder ein Remount kann denselben Wert nicht erneut an Element geben.
 */
export function takeSeatLoginTokenForIframe(seatId: string): string {
  const id = seatId.trim();
  if (!id) return '';
  const token = seatLoginTokens.get(id) || '';
  if (token) seatLoginTokens.delete(id);
  return token;
}

export function clearSeatLoginTokens(): void {
  seatLoginTokens.clear();
}

export function readSeatElementEnabled(record: Record<string, unknown>): boolean {
  const flag = record.element_seat;
  return flag === true || flag === 'true' || flag === 1 || flag === '1';
}

/** Reiter Kanzlei-Chat nur am eigenen Sitz, wenn Element an ist. */
export function kanzleiChatNavVisible(elementSeat: boolean | undefined | null): boolean {
  return elementSeat === true;
}

export type KanzleiChatSeat = {
  id: string;
  name: string;
  email?: string;
  element_seat: boolean;
  matrix_room_id: string;
  /** Echte MXID nach Attach. Leer = nicht einladbar. */
  matrix_user_id?: string;
};

/** Room-Id, Alias oder Matrix-User — nie in die Kundenfläche, auch nicht als Name-Fallback. */
export function isTechnicalMatrixToken(raw: string | undefined | null): boolean {
  const s = String(raw ?? '').trim();
  if (!s) return false;
  if (isHonestMatrixRoomId(s) || isStubMatrixRoomId(s)) return true;
  if (s.startsWith('!') || s.startsWith('#')) return true;
  if (s.startsWith('@') && s.includes(':')) return true;
  return false;
}

/** Eine Zeile: Raumname. Fehlt er oder ist er eine Id, kein Homeserver in der Fläche. */
export function kanzleiChatRoomLabel(room: { name?: string }): string {
  const name = typeof room.name === 'string' ? room.name.trim() : '';
  if (name && !isTechnicalMatrixToken(name)) return name;
  return 'Raum';
}

export type KanzleiChatSeatInviteLines = {
  /** Sitzname, wie er am Sitz steht. Leer, wenn keiner da ist. */
  name: string;
  /** Registrierungs-Mail. Leer, wenn sie fehlt oder schon im Namen steht. */
  email: string;
};

function seatRegistrationEmail(raw: string | undefined): string {
  const email = typeof raw === 'string' ? raw.trim() : '';
  if (!email.includes('@') || isTechnicalMatrixToken(email) || /\s/.test(email)) return '';
  return email;
}

/**
 * Einladen, zwei Zeilen: Sitzname, darunter die Mail, mit der der Sitz registriert ist.
 * Ohne Namen nur die Mail, einmal. Nie das Ersatzwort „Sitz“, nie Room-Id, nie Homeserver.
 */
export function kanzleiChatSeatInviteLines(seat: {
  name?: string;
  email?: string;
}): KanzleiChatSeatInviteLines {
  const nameRaw = typeof seat.name === 'string' ? seat.name.trim() : '';
  const name = nameRaw && !isTechnicalMatrixToken(nameRaw) ? nameRaw : '';
  const email = seatRegistrationEmail(seat.email);
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return { name, email };
  }
  if (name) return { name, email: '' };
  return { name: '', email };
}

/** aria: dieselben Wörter wie die Zeilen, ohne Tooltip und ohne Room-Id. */
export function kanzleiChatSeatLabel(seat: { name?: string; email?: string }): string {
  const lines = kanzleiChatSeatInviteLines(seat);
  if (lines.name && lines.email) return `${lines.name}, ${lines.email}`;
  if (lines.name) return lines.name;
  if (lines.email) return lines.email;
  return 'Zugang ohne Anzeigename';
}

export function readKanzleiChatSeat(record: Record<string, unknown>): KanzleiChatSeat | null {
  const id = String(record.id || '').trim();
  if (!id) return null;
  return {
    id,
    name: typeof record.name === 'string' ? record.name.trim() : '',
    email: typeof record.email === 'string' ? record.email.trim() : '',
    element_seat: readSeatElementEnabled(record),
    matrix_room_id: readSeatMatrixRoomId(record),
    matrix_user_id: readSeatMatrixUserId(record),
  };
}

/**
 * Einladbar nur mit Element-Flag und echter MXID.
 * office@ ohne `matrix_user_id` und Stub-Domain bleiben draussen.
 */
export function seatHasInvitableElementAccount(seat: {
  id?: string;
  element_seat?: boolean;
  matrix_user_id?: string | null;
} | null | undefined): boolean {
  const id = String(seat?.id ?? '').trim();
  if (!id || seat?.element_seat !== true) return false;
  const mxid = String(seat.matrix_user_id ?? '').trim();
  return isMatrixUserId(mxid) && !isStubMatrixRoomId(mxid);
}

/** Einladen nur Sitze auf diesem Server mit Element-Konto. Andere nicht anbieten. */
export function seatsForKanzleiInvite(seats: KanzleiChatSeat[]): KanzleiChatSeat[] {
  const out: KanzleiChatSeat[] = [];
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    if (!seatHasInvitableElementAccount(seat)) continue;
    out.push(seat);
  }
  return out;
}

/**
 * Raumliste = GET `/api/arkorbit/element-rooms` (Stub verworfen).
 * Sitz-Deep-Links und `matrix_room_id` fügen keinen Raum hinzu.
 * Leere und archivierte Räume fehlen, sobald GET sie nicht mehr liefert.
 */
export function kanzleiChatRooms(input: {
  apiRooms: ElementRoomOption[];
  seats?: Array<{ matrix_room_id?: string }>;
  selfRoomId?: string;
}): ElementRoomOption[] {
  const api = filterHonestElementRooms(input.apiRooms);
  const decorated = api.map((room) => {
    const kept: ElementRoomOption = {
      ...room,
      name: room.name,
      alias: room.alias,
      is_standard: room.is_standard === true,
    };
    if (Array.isArray(room.seat_ids)) kept.seat_ids = room.seat_ids;
    return kept;
  });
  decorated.sort((a, b) => {
    const aStd = a.is_standard === true;
    const bStd = b.is_standard === true;
    if (aStd !== bStd) return aStd ? -1 : 1;
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });
  return decorated;
}

export type KanzleiChatCreateRequest =
  | { ok: true; body: { name: string; seat_ids: string[] } }
  | { ok: false; message: string };

/** POST-Body für /api/arkorbit/element-rooms. seat_ids nur angehakte Element-Sitze. Der Hook nimmt auth.id. */
export function kanzleiChatCreateRequest(input: {
  name: string;
  seatIds: string[];
  inviteSeats: KanzleiChatSeat[];
}): KanzleiChatCreateRequest {
  const name = String(input.name || '').trim();
  if (!name || name.length > 120 || /[\r\n]/.test(name)) {
    return { ok: false, message: 'Name fehlt oder ist ungültig.' };
  }
  const picked = pickElementSeatIds(input.seatIds, input.inviteSeats, {});
  if (!picked.ok) return picked;
  return { ok: true, body: { name, seat_ids: picked.seat_ids } };
}

export type KanzleiChatInviteExistingRequest =
  | { ok: true; body: { room_id: string; seat_ids: string[] } }
  | { ok: false; message: string };

/**
 * Nachladen in einen bestehenden Raum.
 * POST { room_id, seat_ids } ohne name. Nur Element-Sitze, die GET noch nicht führt.
 */
export function kanzleiChatInviteExistingRequest(input: {
  roomId: string;
  seatIds: string[];
  inviteSeats: KanzleiChatSeat[];
  alreadySeatIds?: string[];
}): KanzleiChatInviteExistingRequest {
  const roomId = String(input.roomId || '').trim();
  if (!roomId || !isHonestMatrixRoomId(roomId) || isStubMatrixRoomId(roomId)) {
    return { ok: false, message: 'Raum-Kennung ungültig.' };
  }
  const skip: Record<string, true> = {};
  const already = input.alreadySeatIds || [];
  for (let i = 0; i < already.length; i++) {
    const id = String(already[i] || '').trim();
    if (id) skip[id] = true;
  }
  const picked = pickElementSeatIds(input.seatIds, input.inviteSeats, skip);
  if (!picked.ok) return picked;
  if (!picked.seat_ids.length) {
    return { ok: false, message: 'Keine Sitze zum Einladen.' };
  }
  return { ok: true, body: { room_id: roomId, seat_ids: picked.seat_ids } };
}

/** HTTP 200 ohne invited_seat_ids ist kein Erfolg. Jede angefragte Id muss drinstehen. */
export function kanzleiChatInviteExistingResult(
  data: Record<string, unknown> | null | undefined,
  expectedSeatIds: string[],
  httpStatus?: number,
): { ok: true; roomId: string; invitedSeatIds: string[] } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  const roomId = typeof rec.room_id === 'string' ? rec.room_id.trim() : '';
  const raw = rec.invited_seat_ids;
  const invited: string[] = [];
  if (Array.isArray(raw)) {
    for (let i = 0; i < raw.length; i++) invited.push(String(raw[i] ?? '').trim());
  }
  const wanted = (expectedSeatIds || []).map((id) => String(id || '').trim()).filter(Boolean);
  const covered =
    wanted.length > 0 &&
    wanted.every((id) => invited.includes(id));
  if ((httpStatus == null || httpStatus === 200) && rec.ok === true && Array.isArray(raw) && covered) {
    return { ok: true, roomId, invitedSeatIds: invited };
  }
  const err = typeof rec.error === 'string' ? rec.error.trim() : '';
  const message = typeof rec.message === 'string' ? rec.message.trim() : '';
  return { ok: false, message: err || message || 'Einladen nicht bestätigt.' };
}

function pickElementSeatIds(
  seatIds: string[],
  inviteSeats: KanzleiChatSeat[],
  skip: Record<string, true>,
): { ok: true; seat_ids: string[] } | { ok: false; message: string } {
  const allowed: Record<string, true> = {};
  const seats = inviteSeats || [];
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    if (!seatHasInvitableElementAccount(seat)) continue;
    const id = String(seat.id || '').trim();
    if (id) allowed[id] = true;
  }
  const requested = seatIds || [];
  const seat_ids: string[] = [];
  const seen: Record<string, true> = {};
  for (let i = 0; i < requested.length; i++) {
    const id = String(requested[i] || '').trim();
    if (!id || seen[id] || skip[id]) continue;
    if (!allowed[id]) {
      return { ok: false, message: 'Einladen nur Sitze mit Element-Konto.' };
    }
    seen[id] = true;
    seat_ids.push(id);
    if (seat_ids.length > 50) {
      return { ok: false, message: 'Zu viele Sitze.' };
    }
  }
  return { ok: true, seat_ids };
}

/** Leer, wenn GET für den Raum keine seat_ids liefert. Kein Hinweis, die Funktion fehle. */
export const KANZLEI_CHAT_ROOM_NO_STORED_SEATS =
  'Für diesen Raum sind keine Sitze hinterlegt.';

/**
 * „Im Raum“ nur aus GET `rooms[].seat_ids`.
 * Name und Mail kommen aus der Sitzliste. Unbekannte IDs und `members` bleiben draußen.
 */
export function seatsInSelectedKanzleiRoom(
  seats: KanzleiChatSeat[],
  room: Pick<ElementRoomOption, 'id' | 'seat_ids'> | null | undefined,
): KanzleiChatSeat[] {
  if (!room) return [];
  const roomId = String(room.id || '').trim();
  if (!roomId || !isHonestMatrixRoomId(roomId) || isStubMatrixRoomId(roomId)) return [];
  if (!Array.isArray(room.seat_ids) || room.seat_ids.length === 0) return [];
  const byId: Record<string, KanzleiChatSeat> = {};
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    const id = String(seat?.id || '').trim();
    if (id) byId[id] = seat;
  }
  const out: KanzleiChatSeat[] = [];
  const seen: Record<string, true> = {};
  for (let i = 0; i < room.seat_ids.length; i++) {
    const id = String(room.seat_ids[i] ?? '').trim();
    if (!id || seen[id] || id.charAt(0) === '@' || id.charAt(0) === '!') continue;
    const known = byId[id];
    if (!known) continue;
    seen[id] = true;
    out.push(known);
  }
  return out;
}

/** Aufruf ging raus, Beweis fehlt. Nicht der alte Satz aus der Zeit vor DELETE. */
export const KANZLEI_CHAT_REMOVE_UNPROVEN = 'Entfernen nicht bestätigt.';

/** Aufruf selbst fehlgeschlagen, Antwort ohne eigenen Grund. */
export const KANZLEI_CHAT_REMOVE_FAILED = 'Entfernen fehlgeschlagen.';

export function kanzleiChatRemoveFailureMessage(
  data: Record<string, unknown> | null | undefined,
  httpStatus?: number,
): string {
  const rec = data && typeof data === 'object' ? data : {};
  const err = typeof rec.error === 'string' ? rec.error.trim() : '';
  const message = typeof rec.message === 'string' ? rec.message.trim() : '';
  const reason = err || message;
  if (reason) return reason;
  if (httpStatus != null && httpStatus !== 200) return KANZLEI_CHAT_REMOVE_FAILED;
  return KANZLEI_CHAT_REMOVE_UNPROVEN;
}

export type KanzleiChatRemoveRequest =
  | {
      ok: true;
      mode: 'kick';
      body: { room_id: string; seat_id: string };
      /** Nur wenn der Client keinen DELETE-Body schicken kann. */
      query: string;
    }
  | {
      ok: true;
      mode: 'archive';
      body: { room_id: string };
      /** Nur wenn der Client keinen DELETE-Body schicken kann. Kein seat_id. */
      query: string;
    }
  | { ok: false; message: string };

/** DELETE-Body `{ room_id, seat_id }`. Query nur als Fallback, kein zweiter Pfad. */
export function kanzleiChatRemoveRequest(input: {
  roomId: string;
  seatId: string;
}): KanzleiChatRemoveRequest {
  const roomId = String(input.roomId || '').trim();
  const seatId = String(input.seatId || '').trim();
  if (!roomId || !isHonestMatrixRoomId(roomId) || isStubMatrixRoomId(roomId)) {
    return { ok: false, message: 'Raum-Kennung ungültig.' };
  }
  if (!seatId) {
    return { ok: false, message: 'Eigener Sitz fehlt.' };
  }
  if (seatId.charAt(0) === '@' || seatId.length > 64) {
    return { ok: false, message: 'Sitz-Kennung ungültig.' };
  }
  const body = { room_id: roomId, seat_id: seatId };
  const query = `room_id=${encodeURIComponent(roomId)}&seat_id=${encodeURIComponent(seatId)}`;
  return { ok: true, mode: 'kick', body, query };
}

/**
 * Sichtbare Sitze: Kick des angemeldeten Sitzes.
 * `seat_ids: []`: Archiv, nur `{ room_id }`, kein eigener Sitz.
 * Fehlende `seat_ids` bleiben Kick — Archiv nur bei belegter leerer Liste.
 */
export function kanzleiChatRemoveOwnSeatRequest(input: {
  roomId: string;
  ownSeatId: string;
  seatIds?: string[] | null;
}): KanzleiChatRemoveRequest {
  const roomId = String(input.roomId || '').trim();
  if (Array.isArray(input.seatIds) && input.seatIds.length === 0) {
    if (!roomId || !isHonestMatrixRoomId(roomId) || isStubMatrixRoomId(roomId)) {
      return { ok: false, message: 'Raum-Kennung ungültig.' };
    }
    return {
      ok: true,
      mode: 'archive',
      body: { room_id: roomId },
      query: `room_id=${encodeURIComponent(roomId)}`,
    };
  }
  return kanzleiChatRemoveRequest({
    roomId: input.roomId,
    seatId: input.ownSeatId,
  });
}

/**
 * Erfolg nur bei `ok: true` und `left: true` und derselben room_id/seat_id.
 * HTTP 200 ohne diesen Beweis ist kein Erfolg. Fehlschlag nennt den Grund aus der Antwort.
 */
export function kanzleiChatRemoveResult(
  data: Record<string, unknown> | null | undefined,
  expected: { roomId: string; seatId: string },
  httpStatus?: number,
): { ok: true; roomId: string; seatId: string } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  const roomId = typeof rec.room_id === 'string' ? rec.room_id.trim() : '';
  const seatId = typeof rec.seat_id === 'string' ? rec.seat_id.trim() : '';
  const wantRoom = String(expected.roomId || '').trim();
  const wantSeat = String(expected.seatId || '').trim();
  if (
    (httpStatus == null || httpStatus === 200) &&
    rec.ok === true &&
    rec.left === true &&
    roomId === wantRoom &&
    seatId === wantSeat &&
    wantRoom &&
    wantSeat
  ) {
    return { ok: true, roomId, seatId };
  }
  return { ok: false, message: kanzleiChatRemoveFailureMessage(data, httpStatus) };
}

/**
 * Archiv eines leeren Katalograums. Erfolg nur bei `ok: true` und `catalog_hidden: true`
 * und derselben `room_id`. `left` allein ist kein Archiv. HTTP 200 ohne den Beweis ist kein Erfolg.
 */
export function kanzleiChatArchiveEmptyResult(
  data: Record<string, unknown> | null | undefined,
  expected: { roomId: string },
  httpStatus?: number,
): { ok: true; roomId: string } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  const roomId = typeof rec.room_id === 'string' ? rec.room_id.trim() : '';
  const wantRoom = String(expected.roomId || '').trim();
  if (
    (httpStatus == null || httpStatus === 200) &&
    rec.ok === true &&
    rec.catalog_hidden === true &&
    rec.in_catalog === false &&
    roomId === wantRoom &&
    wantRoom
  ) {
    return { ok: true, roomId };
  }
  return { ok: false, message: kanzleiChatRemoveFailureMessage(data, httpStatus) };
}

/**
 * Raum aus der Liste nur wenn das Archiv `catalog_hidden` belegt hat
 * und das folgende GET diese `room_id` nicht mehr enthält.
 */
export function kanzleiChatEmptyRoomDropped(
  archiveOk: boolean,
  rooms: Array<{ id?: string }>,
  roomId: string,
): boolean {
  if (!archiveOk) return false;
  return !kanzleiChatCatalogKeepsRoom(rooms, roomId);
}

/**
 * Vertrag `a7d5a0ea`: GET liefert leere und archivierte Räume nicht.
 * Die Liste danach ist diese GET-Liste. Kein lokales Verstecken, kein Katalog-Satz.
 */

/** True, wenn GET denselben Raum noch liefert. Sonst fällt er aus der Liste. */
export function kanzleiChatCatalogKeepsRoom(
  rooms: Array<{ id?: string }>,
  roomId: string,
): boolean {
  const id = String(roomId || '').trim();
  if (!id) return true;
  for (let i = 0; i < rooms.length; i++) {
    if (String(rooms[i]?.id || '').trim() === id) return true;
  }
  return false;
}

/** Sitz aus „Im Raum“ nehmen. Die Raumliste bleibt. */
export function omitSeatLeftKanzleiRoom<T extends { id: string }>(
  seats: T[],
  leftSeatIds: string[],
): T[] {
  const gone: Record<string, true> = {};
  for (let i = 0; i < leftSeatIds.length; i++) {
    const id = String(leftSeatIds[i] || '').trim();
    if (id) gone[id] = true;
  }
  if (!Object.keys(gone).length) return seats;
  const out: T[] = [];
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    if (!seat || gone[seat.id]) continue;
    out.push(seat);
  }
  return out;
}

export function kanzleiChatCreateResult(
  data: Record<string, unknown> | null | undefined,
  requestedName: string,
): { ok: true; room: ElementRoomOption } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  const roomId = typeof rec.room_id === 'string' ? rec.room_id.trim() : '';
  const akteId = rec.akte_id;
  if (
    rec.ok !== true ||
    !isHonestMatrixRoomId(roomId) ||
    isStubMatrixRoomId(roomId) ||
    typeof akteId !== 'string' ||
    !Array.isArray(rec.invited_seat_ids)
  ) {
    return { ok: false, message: elementApiFailureText(rec, 'Raum nicht angelegt.') };
  }
  const name = String(requestedName || '').trim();
  return {
    ok: true,
    room: {
      id: roomId,
      alias: '',
      name,
      is_standard: false,
      akte_id: akteId,
    },
  };
}

/** Echte Room-Ids aus dem Sitz-Verzeichnis, das die Kundenliste schon liefert. */
export function roomsFromSeatDirectory(
  members: Array<{ matrix_room_id?: string }>,
): ElementRoomOption[] {
  const out: ElementRoomOption[] = [];
  const seen: Record<string, true> = {};
  for (let i = 0; i < members.length; i++) {
    const id = (members[i].matrix_room_id || '').trim();
    if (!id || seen[id] || !isHonestMatrixRoomId(id) || isStubMatrixRoomId(id)) continue;
    seen[id] = true;
    out.push({ id, alias: '', name: KANZLEI_CHAT_ROOM_LABEL });
  }
  return out;
}

/** API-Namen bleiben, Sitz-Räume füllen nur, was der Stub-Katalog nicht hergibt. */
export function mergeElementRoomOptions(
  fromApi: ElementRoomOption[],
  fromSeats: ElementRoomOption[],
): ElementRoomOption[] {
  const out: ElementRoomOption[] = [];
  const seen: Record<string, true> = {};
  const rows = filterHonestElementRooms(fromApi).concat(filterHonestElementRooms(fromSeats));
  for (let i = 0; i < rows.length; i++) {
    const room = rows[i];
    if (seen[room.id]) continue;
    seen[room.id] = true;
    out.push(room);
  }
  return out;
}

export function isValidElementRoomRef(raw: string): boolean {
  const s = raw.trim();
  if (s.length < 4 || s.length > 255) return false;
  if (/\s/.test(s)) return false;
  if (isStubMatrixRoomId(s)) return false;
  return (s.startsWith('!') || s.startsWith('#')) && s.includes(':');
}

export function sanitizeElementRooms(ids: string[]): string[] {
  const out: string[] = [];
  const seen: Record<string, true> = {};
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i].trim();
    if (!isValidElementRoomRef(id) || seen[id]) continue;
    seen[id] = true;
    out.push(id);
    if (out.length >= 20) break;
  }
  return out;
}

export function parseElementInviteResult(data: Record<string, unknown>): {
  mxid: string;
  inviteLink: string;
} {
  const mxid = typeof data.mxid === 'string' ? data.mxid.trim() : '';
  const inviteLink = typeof data.invite_link === 'string' ? data.invite_link.trim() : '';
  return { mxid, inviteLink };
}

export function hasMatrixPasswordLeak(data: Record<string, unknown>): boolean {
  const keys = ['password', 'matrix_password', 'element_password', 'temp_password'];
  for (let i = 0; i < keys.length; i++) {
    const v = data[keys[i]];
    if (typeof v === 'string' && v.trim()) return true;
  }
  return false;
}

/** PB Schema/Field-Agent: element_seat / element_rooms sind kein customers-Feld. */
export function isPbUnknownElementFieldError(
  status: number,
  data: Record<string, unknown>,
): boolean {
  if (status !== 400) return false;
  const fields =
    data.data && typeof data.data === 'object'
      ? (data.data as Record<string, unknown>)
      : {};
  if ('element_seat' in fields || 'element_rooms' in fields) return true;
  const msg = typeof data.message === 'string' ? data.message.toLowerCase() : '';
  return (
    msg.includes('element_seat') ||
    msg.includes('element_rooms') ||
    msg.includes('unknown field')
  );
}
